import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover"
import { cn } from "../lib/utils"

export function PopoverRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: Popover root is a provider; surface atom
  // contract on a wrapping span (same pattern as dialog/sheet/dropdown).
  const atomDefaults = useAtomDefaults("popover")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Type-based: find trigger/content by type, fallback to position [0] for legacy
  const allChildren = element.children ?? []
  const trigger =
    allChildren.find(c => c.type === "popover-trigger") ?? allChildren[0]
  const contentWrapper = allChildren.find(c => c.type === "popover-content")
  const content = contentWrapper?.children ?? allChildren.filter(c => c !== trigger)

  // defaultOpen: force-open uncontrolled state (preview / initial-open use cases).
  // Matches the pattern already established on alert-dialog-renderer.tsx and
  // collapsible-renderer.tsx. `variant` is orthogonal here (dialog renderer
  // overloads variant="open" but this collides with matrix-cell variant
  // overrides in DS Browser previews).
  const defaultOpen = (element as unknown as Record<string, unknown>).defaultOpen === true

  const handleOpenChange = (open: boolean) => {
    dispatchEvent(element.id, open ? "open" : "close", {})
  }

  return (
    <span
      data-atom-type="popover"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <Popover defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
        {/* base-ui uses `render` (ReactElement) for composition; asChild is a Radix
            pattern and was silently ignored here pre-migration. The rendered
            element receives merged props (ref, aria, data-*) from PopoverTrigger. */}
        <PopoverTrigger
          nativeButton={false}
          className={cn("cursor-pointer", element.className)}
          data-testid={`popover-${element.id}`}
          render={<div>{trigger && <ElementRenderer element={trigger} />}</div>}
        />
        {content && content.length > 0 && (
          <PopoverContent>
            {content.map((child) => (
              <ElementRenderer key={child.id} element={child} />
            ))}
          </PopoverContent>
        )}
      </Popover>
    </span>
  )
}
